import { Card, CardContent } from "@/components/ui/card";
import { Briefcase, Activity } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type EmploymentHistory = {
  id: string;
  position: string | null;
  start_date: string | null;
  end_date: string | null;
  is_current: boolean | null;
  company_name: string | null;
  clients?: {
    name: string;
  };
};

type PersonActivity = {
  id: string;
  type: string;
  subject: string;
  activity_date: string;
  projects?: {
    name: string;
  };
  proposals?: {
    title: string;
  };
};

const getActivityIcon = (type: string) => {
  switch (type) {
    case "call": return "📞";
    case "email": return "📧";
    case "meeting": return "📅";
    case "proposal": return "📄";
    default: return "📝";
  }
};

const formatDateDistance = (date: string) => {
  const now = new Date();
  const activityDate = new Date(date);
  const diffInDays = Math.floor((now.getTime() - activityDate.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "Yesterday";
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  return `${Math.floor(diffInDays / 30)} months ago`;
};

interface PersonOverviewProps {
  personId: string;
}

export function PersonOverview({ personId }: PersonOverviewProps) {
  const { data: employmentHistory = [] } = useQuery({
    queryKey: ["employment-history", personId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employment_history")
        .select(`
          *,
          clients (
            name
          )
        `)
        .eq("person_id", personId)
        .order("start_date", { ascending: false });
      
      if (error) throw error;
      return data as EmploymentHistory[];
    },
    enabled: !!personId
  });

  const { data: activities = [] } = useQuery({
    queryKey: ["person-activities", personId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("activities")
        .select(`
          *,
          projects (
            name
          ),
          proposals (
            title
          )
        `)
        .eq("person_id", personId)
        .order("activity_date", { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return data as PersonActivity[];
    },
    enabled: !!personId
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Career summary */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Briefcase className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Career Summary</h3>
            </div>
            <div className="space-y-3">
              {employmentHistory.slice(0, 3).map((job, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">{job.position}</div>
                    <div className="text-xs text-muted-foreground">
                      {job.clients?.name || job.company_name}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {job.start_date ? new Date(job.start_date).getFullYear() : ""}-
                    {job.is_current ? "Present" : job.end_date ? new Date(job.end_date).getFullYear() : ""}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent activity */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Activity className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Recent Activity</h3>
            </div>
            <div className="space-y-3">
              {activities.slice(0, 3).map((activity, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <span className="text-lg mt-0.5">{getActivityIcon(activity.type)}</span>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{activity.subject}</div>
                    <div className="text-xs text-muted-foreground">
                      {activity.projects?.name && `${activity.projects.name} • `}
                      {activity.proposals?.title && `${activity.proposals.title} • `}
                      {formatDateDistance(activity.activity_date)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}